import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";

type IconType = React.ElementType | React.FunctionComponent<React.SVGProps<SVGSVGElement>>;

export type TrendType = "up" | "down" | "neutral";

export interface DashboardMetricCardProps {
  value: string;
  /** Primary label — use `title` or `label` (alias for drop-in compatibility). */
  title?: string;
  label?: string;
  icon?: IconType;
  trendChange?: string;
  trendType?: TrendType;
  /** Numeric delta from calcDelta() — converted to trendType/trendChange internally. */
  delta?: number | null;
  /** When true, a negative delta is "good" (lower is better, e.g. cost). */
  invertDelta?: boolean;
  /** Accepted but not rendered — kept for API compatibility. */
  variant?: string;
  totalLabel?: string;
  /** Per-platform split chips (e.g. Meta vs Google), shown below the value. */
  breakdowns?: { label: string; value: string }[];
  className?: string;
  /** Optional warning/info text shown below the value in amber. */
  warning?: string;
}

// Platform color map for breakdown chips (Meta / Google / Allegro / Organic).
const PLATFORM_BREAKDOWN_STYLES: Record<string, { dot: string; text: string; bg: string }> = {
  meta: { dot: "bg-blue-400", text: "text-blue-300", bg: "bg-blue-500/10" },
  google: { dot: "bg-yellow-400", text: "text-yellow-300", bg: "bg-yellow-500/10" },
  tiktok: { dot: "bg-pink-400", text: "text-pink-300", bg: "bg-pink-500/10" },
  allegro: { dot: "bg-orange-400", text: "text-orange-300", bg: "bg-orange-500/10" },
  organic: { dot: "bg-emerald-400", text: "text-emerald-300", bg: "bg-emerald-500/10" },
};
const DEFAULT_BREAKDOWN_STYLE = {
  dot: "bg-muted",
  text: "text-muted-foreground",
  bg: "bg-muted/60",
};

function breakdownStyle(label: string) {
  const lower = label.toLowerCase();
  if (lower.startsWith("google")) return PLATFORM_BREAKDOWN_STYLES.google;
  // VP-134: chipy per źródło organiczne ("YOUTUBE ORGANIC") mają wspólny, zielony styl —
  // pierwsze słowo to nazwa źródła, więc lookup po nim trafiłby w domyślny szary.
  if (lower.includes("organic")) return PLATFORM_BREAKDOWN_STYLES.organic;
  return PLATFORM_BREAKDOWN_STYLES[lower.split(" ")[0]] || DEFAULT_BREAKDOWN_STYLE;
}

const TREND_ICON: Record<TrendType, React.ElementType> = {
  up: ArrowUp,
  down: ArrowDown,
  neutral: Minus,
};

const TREND_COLOR: Record<TrendType, string> = {
  up: "text-emerald-400",
  down: "text-red-400",
  neutral: "text-muted-foreground",
};

const TREND_LABEL: Record<TrendType, string> = {
  up: "wzrost",
  down: "spadek",
  neutral: "zmiana",
};

function resolveTrend(
  delta: number | null | undefined,
  invertDelta: boolean | undefined,
  explicitType: TrendType | undefined,
  explicitChange: string | undefined
): { resolvedType: TrendType; resolvedChange: string | undefined } {
  if (explicitChange)
    return { resolvedType: explicitType ?? "neutral", resolvedChange: explicitChange };
  if (delta == null || !isFinite(delta))
    return { resolvedType: "neutral", resolvedChange: undefined };
  const isGood = invertDelta ? delta < 0 : delta > 0;
  const isBad = invertDelta ? delta > 0 : delta < 0;
  const resolvedType: TrendType = isGood ? "up" : isBad ? "down" : "neutral";
  return { resolvedType, resolvedChange: `${delta > 0 ? "+" : ""}${delta.toFixed(1)}%` };
}

const DashboardMetricCard: React.FC<DashboardMetricCardProps> = ({
  value,
  title,
  label,
  icon: IconComponent,
  trendChange,
  trendType,
  delta,
  invertDelta,
  className,
  warning,
  totalLabel,
  breakdowns,
  // accepted but unused props kept for API compatibility
  variant: _variant,
}) => {
  const heading = title ?? label ?? "";
  const { resolvedType, resolvedChange } = resolveTrend(delta, invertDelta, trendType, trendChange);
  const TrendIcon = TREND_ICON[resolvedType];
  const trendColorClass = TREND_COLOR[resolvedType];

  return (
    <motion.div
      whileHover={{
        y: -4,
        boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.4), 0 4px 6px -4px rgb(0 0 0 / 0.3)",
      }}
      transition={{ type: "spring", stiffness: 400, damping: 20 }}
      className={cn("cursor-pointer rounded-lg", className)}
    >
      <Card className="h-full transition-colors duration-200 bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 px-6 pt-6">
          <CardTitle className="text-caption font-light text-muted-foreground uppercase tracking-wider">
            {heading}
          </CardTitle>
          {IconComponent && (
            <IconComponent className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          )}
        </CardHeader>
        <CardContent className="px-6 pb-6 pt-0">
          <div className="text-heading-2 font-extrabold text-foreground mb-3">{value}</div>
          {resolvedChange && (
            <p className={cn("flex items-center text-caption font-light", trendColorClass)}>
              <TrendIcon className="h-3 w-3 mr-1" aria-hidden="true" />
              {resolvedChange} {TREND_LABEL[resolvedType]}
            </p>
          )}
          {warning && <p className="text-[10px] text-amber-400/80 mt-1 leading-tight">{warning}</p>}
          {breakdowns && breakdowns.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {totalLabel && (
                <span className="w-full text-[9px] text-muted-foreground font-light uppercase tracking-wider">
                  {totalLabel}
                </span>
              )}
              {breakdowns.map((b) => {
                const style = breakdownStyle(b.label);
                return (
                  <div
                    key={b.label}
                    className={cn(
                      "flex items-center gap-1.5 rounded-md px-2 py-1 bg-[#ef3e29]/15 border border-[#ef3e29]/30"
                    )}
                  >
                    <span className={cn("w-1.5 h-1.5 rounded-full", style.dot)} />
                    <span className={cn("text-[11px] font-extrabold", style.text)}>{b.value}</span>
                    <span className="text-[10px] font-light text-muted-foreground">{b.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default DashboardMetricCard;
